// /settings/notifications — каналы доставки + типы событий + тихие часы.
// Реальные notifications.getPreferences / updatePreferences (Schema-First
// enforced). Без сохранения пользователь продолжал бы получать отключённые
// пуши. Autosave — debounced (300мс) partial update.
//
// Канал «Push в браузере» (webPush) — не просто флаг: включение запрашивает
// Notification.permission, подписывает через PushManager (VAPID) и регистрирует
// токен на сервере (useWebPushChannel). Выключение — отписка + удаление токена.
// Help-текст отражает реальный Notification.permission, а не статичную строку.
//
// Время SRS-напоминания живёт здесь же (раньше — в «Внешнем виде»): тумблер
// типа `srsReminder` пишет notification_preferences.srsReminderEnabled, сервер
// зеркалит его в users.srs_reminder_enabled (updatePreferences + PowerSync
// notifications/sync.ts), а час — прямой PowerSync-UPDATE users.srs_reminder_hour
// (поле в whitelist users-write-handler). Именно эти две колонки читает cron
// sendSrsReminder, поэтому дубля-тумблера во «Внешнем виде» больше нет.
// См. docs/ui-spec/settings.notifications.{desktop,mobile}.md.

import { Input, Switch } from "@studyhub/ui";
import { toast } from "@studyhub/ui/toast";
import {
	IconAlertTriangle,
	IconBell,
	IconCalendar,
	IconCards,
	IconCreditCard,
	IconDeviceMobile,
	IconFlame,
	IconLock,
	IconMail,
	IconMessageDots,
	IconMoon,
	IconNews,
	IconShieldLock,
	IconSparkles,
	IconSpeakerphone,
	IconWand,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SettingsPageContainer, SettingsPageHeader } from "@/components/settings/page-header";
import { SettingsSection } from "@/components/settings/settings-shell";
import { HelpHint } from "@/components/shared/help-hint";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import { useSession } from "@/hooks/use-session";
import { useWebPushChannel } from "@/hooks/use-web-push-channel";
import { cn } from "@/lib/cn";
import { useCommerceVisible } from "@/lib/module-availability/commerce";
import { IS_CAPACITOR } from "@/lib/platform-detect";
import { getPowerSync } from "@/lib/powersync/client";
import { usePowerSyncQuery } from "@/lib/powersync/use-query";
import { browserTimezoneOrUtc } from "@/lib/timezones";
import { api } from "@/lib/trpc-client";

export const Route = createFileRoute("/settings/notifications")({
	component: NotificationsRoute,
	pendingComponent: PagePendingFallback,
});

// Маппинг UI-ключей на поля контракта notificationPreferencesSchema.
const CHANNEL_FIELD = {
	webPush: "webPushEnabled",
	mobilePush: "mobilePushEnabled",
	email: "emailEnabled",
	inApp: "inAppEnabled",
} as const;

/**
 * Ответ сервера, когда включение маркетинга запрещено журналом согласий
 * экосистемы. Строка приходит в `message` как есть — сверяем ею, а не кодом:
 * `PRECONDITION_FAILED` носят и другие отказы.
 */
const MARKETING_REVOKED = "iec-id:consents.marketingRevoked";

const TYPE_FIELD = {
	aiReady: "aiTaskCompleteEnabled",
	srsReminder: "srsReminderEnabled",
	streaks: "streakNudgeEnabled",
	quota: "quotaWarningEnabled",
	subscription: "subscriptionAlertEnabled",
	security: "securityAlertEnabled",
	product: "productUpdatesEnabled",
	marketing: "marketingEnabled",
	recommendations: "recommendationsEnabled",
	digest: "weeklyDigestEnabled",
} as const;

type ChannelKey = keyof typeof CHANNEL_FIELD;
type TypeKey = keyof typeof TYPE_FIELD;

interface Channel {
	key: ChannelKey;
	icon: typeof IconBell;
}

const CHANNELS: Channel[] = [
	{ key: "webPush", icon: IconBell },
	{ key: "mobilePush", icon: IconDeviceMobile },
	{ key: "email", icon: IconMail },
	{ key: "inApp", icon: IconMessageDots },
];

interface EventType {
	key: TypeKey;
	icon: typeof IconBell;
	locked?: boolean;
}

const TYPES: EventType[] = [
	{ key: "aiReady", icon: IconSparkles },
	{ key: "srsReminder", icon: IconCards },
	{ key: "streaks", icon: IconFlame },
	{ key: "quota", icon: IconAlertTriangle },
	{ key: "subscription", icon: IconCreditCard },
	{ key: "security", icon: IconShieldLock, locked: true },
	{ key: "product", icon: IconNews },
	{ key: "marketing", icon: IconSpeakerphone },
	// Ключ `recommendations` экосистемы IEC ID (§3.4 гайда): персональные подборки.
	// Отзыв из витрины приходит событием `consent-revoked` и гасит этот тумблер.
	{ key: "recommendations", icon: IconWand },
	{ key: "digest", icon: IconCalendar },
];

/** "HH:MM" → час (0-23): users.srs_reminder_hour хранит именно час. */
function hourFromTimeStr(v: string): number {
	const h = Number.parseInt(v.split(":")[0] ?? "0", 10);
	return Number.isFinite(h) ? Math.min(23, Math.max(0, h)) : 0;
}
/** Час (0-23) → "HH:00" для <input type="time">. */
function timeStrFromHour(h: number): string {
	return `${String(h).padStart(2, "0")}:00`;
}

function NotificationsRoute() {
	const { t } = useTranslation("settings");
	const commerceVisible = useCommerceVisible();
	const webPush = useWebPushChannel();
	const { user: me } = useSession();
	const prefsQuery = api.notifications.getPreferences.useQuery(undefined, {
		staleTime: 30_000,
	});
	const updateMutation = api.notifications.updatePreferences.useMutation();
	// Тихие часы интерпретируются в timezone профиля (как SRS), не устройства.
	// Час SRS-напоминания живёт в users (не в notification_preferences) —
	// его читает cron sendSrsReminder.
	const localUser = usePowerSyncQuery<{
		timezone: string | null;
		srs_reminder_hour: number | null;
	}>(`SELECT timezone, srs_reminder_hour FROM users WHERE id = ? LIMIT 1`, me?.id ? [me.id] : []);
	const profileTimezone = localUser.data?.[0]?.timezone ?? browserTimezoneOrUtc();
	const srsHourFromDb = localUser.data?.[0]?.srs_reminder_hour;

	// Local default webPush=false — совпадает с серверным DEFAULT_PREFERENCES,
	// чтобы тумблер не «мигал» с true на false после гидратации.
	const [channels, setChannels] = useState<Record<ChannelKey, boolean>>({
		webPush: false,
		mobilePush: true,
		email: true,
		inApp: true,
	});
	const [types, setTypes] = useState<Record<TypeKey, boolean>>({
		aiReady: true,
		srsReminder: true,
		streaks: false,
		quota: true,
		subscription: true,
		security: true,
		// Совпадают с серверным DEFAULT_PREFERENCES — иначе тумблер «мигает» с
		// локального значения на серверное после гидратации. `marketing` и
		// `recommendations` разрешены по умолчанию: модель согласий экосистемы —
		// «канал разрешён, пока не пришёл отзыв» (§3.4 гайда IEC ID).
		product: true,
		marketing: true,
		recommendations: true,
		digest: true,
	});
	const [quietEnabled, setQuietEnabled] = useState(false);
	const [quietFrom, setQuietFrom] = useState("22:00");
	const [quietTo, setQuietTo] = useState("08:00");
	const [srsHour, setSrsHour] = useState("19:00");
	const [savedAt, setSavedAt] = useState<Date | null>(null);
	const hydrated = useRef(false);
	const srsHydrated = useRef(false);

	// Гидратация часа напоминания из users (PowerSync). Отдельно от prefs —
	// у них разные источники и разное время прихода.
	useEffect(() => {
		if (srsHourFromDb == null || srsHydrated.current) return;
		srsHydrated.current = true;
		setSrsHour(timeStrFromHour(srsHourFromDb));
	}, [srsHourFromDb]);

	// Гидратация реальными preferences (а не локальными дефолтами).
	useEffect(() => {
		const p = prefsQuery.data;
		if (!p || hydrated.current) return;
		hydrated.current = true;
		setChannels({
			webPush: p.webPushEnabled,
			mobilePush: p.mobilePushEnabled,
			email: p.emailEnabled,
			inApp: p.inAppEnabled,
		});
		setTypes({
			aiReady: p.aiTaskCompleteEnabled,
			srsReminder: p.srsReminderEnabled,
			streaks: p.streakNudgeEnabled,
			quota: p.quotaWarningEnabled,
			subscription: p.subscriptionAlertEnabled,
			security: p.securityAlertEnabled,
			product: p.productUpdatesEnabled,
			marketing: p.marketingEnabled,
			recommendations: p.recommendationsEnabled,
			digest: p.weeklyDigestEnabled,
		});
		setQuietEnabled(p.quietHoursEnabled);
		setQuietFrom(p.quietHoursStart);
		setQuietTo(p.quietHoursEnd);
		setSavedAt(new Date());
	}, [prefsQuery.data]);

	// Debounced update — мержим патчи: при серии тумблеров за <300ms
	// clearTimeout иначе оставлял только последний ключ (UI OFF, БД — старое).
	// securityAlertEnabled НЕ отправляем (контракт: всегда true).
	const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pendingPatch = useRef<Record<string, boolean | string>>({});
	const flushPrefs = (merged: Record<string, boolean | string>, isRetry = false) => {
		updateMutation.mutate(merged, {
			onSuccess: () => {
				setSavedAt(new Date());
				void prefsQuery.refetch();
			},
			onError: (error) => {
				// Отказ по отозванному согласию — не сбой связи, а ответ по существу.
				// Повторять его бессмысленно (следующая попытка получит тот же
				// отказ), а общая фраза «не удалось сохранить» здесь врёт: сохранить
				// не дали намеренно, и человеку нужно знать, где это решается.
				if (error.message === MARKETING_REVOKED) {
					toast.error(t("notifications.marketingRevoked"));
					void prefsQuery.refetch();
					return;
				}
				pendingPatch.current = { ...merged, ...pendingPatch.current };
				toast.error(t("notifications.saveError"));
				if (isRetry) return;
				if (debounce.current) clearTimeout(debounce.current);
				debounce.current = setTimeout(() => {
					const retry = pendingPatch.current;
					pendingPatch.current = {};
					if (Object.keys(retry).length === 0) return;
					flushPrefs(retry, true);
				}, 1500);
			},
		});
	};
	function persist(patch: Record<string, boolean | string>) {
		setSavedAt(null);
		pendingPatch.current = { ...pendingPatch.current, ...patch };
		if (debounce.current) clearTimeout(debounce.current);
		debounce.current = setTimeout(() => {
			const merged = pendingPatch.current;
			pendingPatch.current = {};
			flushPrefs(merged);
		}, 300);
	}

	// Час напоминания — не часть notification_preferences: cron sendSrsReminder
	// фильтрует по users.srs_reminder_hour, поэтому пишем прямо в users
	// (поле в whitelist users-write-handler, апload идёт очередью PowerSync).
	function persistSrsHour(nextHourStr: string) {
		if (!me?.id) return;
		void getPowerSync()
			.execute(`UPDATE users SET srs_reminder_hour = ?, updated_at = ? WHERE id = ?`, [
				hourFromTimeStr(nextHourStr),
				new Date().toISOString(),
				me.id,
			])
			.then(() => setSavedAt(new Date()))
			.catch(() => toast.error(t("notifications.saveError")));
	}

	// Тумблер webPush: side-effect (permission + subscribe + register/unregister),
	// а не только persist флага.
	async function handleWebPushToggle(next: boolean) {
		if (next) {
			const res = await webPush.enable();
			if (res.ok) {
				setChannels((p) => ({ ...p, webPush: true }));
				persist({ webPushEnabled: true });
				toast.success(t("notifications.webPushError.enabled"));
			} else {
				// Подписка не удалась — оставляем тумблер выключенным.
				setChannels((p) => ({ ...p, webPush: false }));
				toast.error(t(`notifications.webPushError.${res.reason}`));
			}
			return;
		}
		setChannels((p) => ({ ...p, webPush: false }));
		persist({ webPushEnabled: false });
		await webPush.disable();
	}

	// Динамический help-текст webPush из реального Notification.permission.
	function webPushHelp(): string {
		if (!webPush.supported) return t("notifications.channelHelp.webPushUnsupported");
		if (!webPush.vapidConfigured) return t("notifications.channelHelp.webPushNoVapid");
		if (webPush.permission === "denied") return t("notifications.channelHelp.webPushDenied");
		if (webPush.permission === "granted") return t("notifications.channelHelp.webPushGranted");
		return t("notifications.channelHelp.webPushDefault");
	}

	function channelHelp(key: ChannelKey): string {
		if (key === "webPush") return webPushHelp();
		if (key === "mobilePush") {
			return IS_CAPACITOR
				? t("notifications.channelHelp.mobilePushMobile")
				: t("notifications.channelHelp.mobilePushWeb");
		}
		if (key === "inApp") return t("notifications.channelHelp.inApp");
		return "";
	}

	// В приложении из App Store (guideline 3.1.1) тарифной системы не видно:
	// уведомлений об оплате там не бывает, а предупреждение о лимите называется
	// без слова «тариф». Тумблеры и поля настроек при этом те же самые.
	// «Маркетинг» описан как «скидки и предложения тарифов» — та же продажа,
	// поэтому прячется вместе с подписками (отказ 1.1.1 (9) по 3.1.1). Сама
	// настройка не теряется: она общая с сайтом и с IEC ID.
	const visibleTypes = commerceVisible
		? TYPES
		: TYPES.filter((tp) => tp.key !== "subscription" && tp.key !== "marketing");
	const typeCopyKey = (key: EventType["key"]): string =>
		key === "quota" && !commerceVisible ? "quotaNoSale" : key;

	return (
		<SettingsPageContainer>
			<SettingsPageHeader
				title={t("notifications.title")}
				description={t("notifications.subtitle")}
				icon={<IconBell stroke={1.75} />}
			/>

			<SettingsSection title={t("notifications.channels")} icon={<IconBell stroke={1.75} />}>
				{CHANNELS.map((c) => {
					const Icon = c.icon;
					const help = channelHelp(c.key);
					// webPush визуально включён только если разрешение реально granted.
					const isWebPushDenied = c.key === "webPush" && webPush.permission === "denied";
					const checked =
						c.key === "webPush" ? channels.webPush && !isWebPushDenied : channels[c.key];
					return (
						<div
							key={c.key}
							className="flex min-h-[56px] items-center gap-3 px-4 py-3.5 transition-colors duration-fast hover:bg-[var(--surface-2)]"
						>
							<Icon size={18} stroke={1.75} className="shrink-0 text-muted-foreground" />
							<div className="min-w-0 flex-1">
								<div className="text-sm font-medium text-foreground">
									{t(`notifications.channel.${c.key}`)}
								</div>
								{help && <div className="mt-0.5 text-xs text-muted-foreground">{help}</div>}
							</div>
							<Switch
								checked={checked}
								disabled={
									c.key === "webPush" && (webPush.isBusy || !webPush.supported || isWebPushDenied)
								}
								onCheckedChange={(v) => {
									if (c.key === "webPush") {
										void handleWebPushToggle(v);
										return;
									}
									setChannels((p) => ({ ...p, [c.key]: v }));
									persist({ [CHANNEL_FIELD[c.key]]: v });
								}}
							/>
						</div>
					);
				})}
			</SettingsSection>

			<SettingsSection title={t("notifications.types")} icon={<IconSparkles stroke={1.75} />}>
				{visibleTypes.map((tp) => {
					const Icon = tp.icon;
					const copyKey = typeCopyKey(tp.key);
					return (
						<Fragment key={tp.key}>
							<div className="flex min-h-[56px] items-center gap-3 px-4 py-3.5 transition-colors duration-fast hover:bg-[var(--surface-2)]">
								<Icon size={18} stroke={1.75} className="shrink-0 text-muted-foreground" />
								<div className="min-w-0 flex-1">
									<div className="flex items-center gap-1">
										<span className="text-sm font-medium text-foreground">
											{t(`notifications.type.${copyKey}`)}
										</span>
										{/* Названия типов сами по себе не объясняют, что именно придёт —
										    пояснение по «вопросику» (hover на desktop, tap на touch). */}
										<HelpHint content={t(`notifications.typeHelp.${copyKey}`)} />
									</div>
									{tp.locked && (
										<div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
											<IconLock size={11} stroke={1.75} />
											{t("notifications.securityLocked")}
										</div>
									)}
								</div>
								<Switch
									checked={tp.locked ? true : types[tp.key]}
									disabled={tp.locked}
									onCheckedChange={(v) => {
										setTypes((p) => ({ ...p, [tp.key]: v }));
										persist({ [TYPE_FIELD[tp.key]]: v });
									}}
								/>
							</div>
							{tp.key === "srsReminder" && (
								<div
									className={cn(
										"space-y-1.5 bg-[var(--surface-2)]/40 px-4 py-3.5 pl-[3.4rem] transition-opacity",
										!types.srsReminder && "pointer-events-none opacity-50",
									)}
								>
									<label className="flex flex-col gap-1.5">
										<span className="text-sm font-medium text-foreground">
											{t("notifications.srsTime")}
										</span>
										<Input
											type="time"
											value={srsHour}
											onChange={(e) => setSrsHour(e.target.value)}
											onBlur={() => persistSrsHour(srsHour)}
											step={3600}
											disabled={!types.srsReminder}
											className="max-w-32 tabular-nums"
										/>
										<span className="text-xs text-muted-foreground">
											{t("notifications.srsTimeHelp")}{" "}
											{t("notifications.tzNote", { tz: profileTimezone })}
										</span>
									</label>
								</div>
							)}
						</Fragment>
					);
				})}
			</SettingsSection>

			<SettingsSection
				title={t("notifications.quietHours")}
				description={t("notifications.quietHoursDescription")}
				icon={<IconMoon stroke={1.75} />}
			>
				<div className="flex min-h-[56px] items-center gap-3 px-4 py-3.5 transition-colors duration-fast hover:bg-[var(--surface-2)]">
					<IconMoon size={18} stroke={1.75} className="shrink-0 text-muted-foreground" />
					<div className="flex-1 text-sm font-medium text-foreground">
						{t("notifications.quietHours")}
					</div>
					<Switch
						checked={quietEnabled}
						onCheckedChange={(v) => {
							setQuietEnabled(v);
							persist({ quietHoursEnabled: v });
						}}
					/>
				</div>
				<div
					className={cn(
						"grid grid-cols-2 gap-3 px-4 py-3.5 transition-opacity",
						!quietEnabled && "pointer-events-none opacity-50",
					)}
				>
					<label className="flex flex-col gap-1.5">
						<span className="text-xs font-medium text-muted-foreground">
							{t("notifications.quietFrom")}
						</span>
						<Input
							type="time"
							value={quietFrom}
							onChange={(e) => setQuietFrom(e.target.value)}
							onBlur={() => persist({ quietHoursStart: quietFrom })}
							className="tabular-nums"
						/>
					</label>
					<label className="flex flex-col gap-1.5">
						<span className="text-xs font-medium text-muted-foreground">
							{t("notifications.quietTo")}
						</span>
						<Input
							type="time"
							value={quietTo}
							onChange={(e) => setQuietTo(e.target.value)}
							onBlur={() => persist({ quietHoursEnd: quietTo })}
							className="tabular-nums"
						/>
					</label>
				</div>
				<div className="px-4 py-2.5 text-xs text-muted-foreground">
					{t("notifications.tzNote", { tz: profileTimezone })}
				</div>
			</SettingsSection>

			<div className="mb-8 flex items-center gap-1.5 px-4 text-xs tabular-nums text-muted-foreground lg:px-0">
				<span
					className={cn(
						"size-1.5 rounded-full",
						savedAt ? "bg-success" : "animate-pulse bg-warning",
					)}
					aria-hidden
				/>
				{savedAt ? t("savedJust") : t("saving")}
			</div>
		</SettingsPageContainer>
	);
}
